import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import './Voitures.css';

const CARBURANT_LABELS    = { essence: 'Essence', diesel: 'Diesel', electrique: 'Électrique', hybride: 'Hybride' };
const BOITE_LABELS        = { manuelle: 'Manuelle', automatique: 'Automatique' };
const TYPE_ANNONCE_LABELS = { vente: 'À vendre', location: 'À louer' };

function formatPrix(prix) {
  return Number(prix).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function formatKm(km) {
  return Number(km).toLocaleString('fr-FR') + ' km';
}

const MAX_PRIX = 30000;
const MAX_KM   = 500000;
const EMPTY_FILTERS = { carburant: '', boite: '', prixMax: MAX_PRIX, kmMax: MAX_KM };

function Voitures() {
  const [voitures, setVoitures]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [photoIdx, setPhotoIdx]   = useState(0);
  const [filters, setFilters]     = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    axios.get('/api/voitures/')
      .then(res => setVoitures(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  function toggleFilters() {
    if (showFilters) {
      setFilters(EMPTY_FILTERS);
    }
    setShowFilters(v => !v);
  }

  const filtered = useMemo(() => {
    if (!showFilters) return voitures;
    return voitures.filter(v => {
      if (filters.carburant && v.carburant !== filters.carburant) return false;
      if (filters.boite && v.boite !== filters.boite) return false;
      if (filters.prixMax < MAX_PRIX && Number(v.prix) > filters.prixMax) return false;
      if (filters.kmMax < MAX_KM && v.kilometrage > filters.kmMax) return false;
      return true;
    });
  }, [voitures, filters, showFilters]);

  const hasActiveFilters = filters.carburant || filters.boite || filters.prixMax < MAX_PRIX || filters.kmMax < MAX_KM;

  function openLightbox(v) { setSelected(v); setPhotoIdx(0); }
  const prev = useCallback((e) => { e?.stopPropagation(); setPhotoIdx(i => (i - 1 + selected.photos.length) % selected.photos.length); }, [selected]);
  const next = useCallback((e) => { e?.stopPropagation(); setPhotoIdx(i => (i + 1) % selected.photos.length); }, [selected]);

  // Bloquer le scroll de la page quand la lightbox est ouverte
  useEffect(() => {
    document.body.style.overflow = selected ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selected]);

  // Navigation clavier
  useEffect(() => {
    if (!selected || selected.photos?.length <= 1) return;
    function onKey(e) {
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape')     setSelected(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, prev, next]);

  // Swipe tactile
  const touchStart = React.useRef(null);
  function onTouchStart(e) { touchStart.current = e.touches[0].clientX; }
  function onTouchEnd(e) {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) diff > 0 ? next() : prev();
    touchStart.current = null;
  }

  const mainPhoto = v => v.photos?.find(p => p.principale) ?? v.photos?.[0] ?? null;

  return (
    <div className="voitures-page">
      <Helmet>
        <title>Voitures d'Occasion — Eagle Car's | Achat Vente Auto Haute-Savoie (74)</title>
        <meta name="description" content="Achetez ou vendez votre voiture d'occasion chez Eagle Car's à Étrembières (74100). Toutes marques, véhicules contrôlés. Reprise possible. Livraison en Haute-Savoie, Ain et Suisse." />
        <link rel="canonical" href="https://eaglecars.fr/voitures" />
        <meta property="og:title" content="Voitures d'Occasion — Eagle Car's Haute-Savoie" />
        <meta property="og:description" content="Achat et vente de voitures d'occasion toutes marques à Étrembières (74). Reprise possible." />
        <meta property="og:url" content="https://eaglecars.fr/voitures" />
        <meta property="og:image" content="https://eaglecars.fr/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
      </Helmet>
      <div className="page-header container">
        <h1 className="section-title">Notre <span>Sélection</span></h1>
        <div className="gold-line centered" />
        <p className="section-subtitle">
          Découvrez nos véhicules disponibles, sélectionnés avec soin par notre équipe.
        </p>
      </div>

      <div className="container voitures-body">

        {/* ── Bouton Filtres ── */}
        <div className="voit-filters-toggle-row">
          <button className={`voit-filters-toggle${showFilters ? ' active' : ''}`} onClick={toggleFilters}>
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
            </svg>
            {showFilters ? 'Fermer les filtres' : 'Filtres'}
          </button>
          {showFilters && hasActiveFilters && (
            <button className="voit-reset" onClick={() => setFilters(EMPTY_FILTERS)}>✕ Réinitialiser</button>
          )}
        </div>

        {/* ── Panneau de filtres ── */}
        {showFilters && (
          <div className="voit-filters">
            <div className="voit-filters-body">
              <div className="voit-filter-group">
                <label>Carburant</label>
                <div className="voit-pills">
                  {Object.entries(CARBURANT_LABELS).map(([val, label]) => (
                    <button
                      key={val}
                      className={`voit-pill${filters.carburant === val ? ' active' : ''}`}
                      onClick={() => setFilters(f => ({ ...f, carburant: f.carburant === val ? '' : val }))}
                    >{label}</button>
                  ))}
                </div>
              </div>
              <div className="voit-filter-group">
                <label>Boîte</label>
                <div className="voit-pills">
                  {Object.entries(BOITE_LABELS).map(([val, label]) => (
                    <button
                      key={val}
                      className={`voit-pill${filters.boite === val ? ' active' : ''}`}
                      onClick={() => setFilters(f => ({ ...f, boite: f.boite === val ? '' : val }))}
                    >{label}</button>
                  ))}
                </div>
              </div>
              <div className="voit-filter-group">
                <label>Prix max : <span className="voit-slider-val">{Number(filters.prixMax).toLocaleString('fr-FR')} €</span></label>
                <input
                  type="range" min="1000" max="30000" step="1000"
                  value={filters.prixMax}
                  onChange={e => setFilters(f => ({ ...f, prixMax: Number(e.target.value) }))}
                  className="voit-slider"
                />
                <div className="voit-slider-bounds"><span>1 000 €</span><span>30 000 €</span></div>
              </div>
              <div className="voit-filter-group">
                <label>Kilométrage max : <span className="voit-slider-val">{Number(filters.kmMax).toLocaleString('fr-FR')} km</span></label>
                <input
                  type="range" min="0" max="500000" step="5000"
                  value={filters.kmMax}
                  onChange={e => setFilters(f => ({ ...f, kmMax: Number(e.target.value) }))}
                  className="voit-slider"
                />
                <div className="voit-slider-bounds"><span>0 km</span><span>500 000 km</span></div>
              </div>
            </div>
          </div>
        )}

        {/* ── Contenu ── */}
        {loading ? (
          <p className="voit-loading">Chargement...</p>
        ) : filtered.length === 0 ? (
          <div className="voit-empty">
            <p>{voitures.length === 0 ? 'Aucun véhicule disponible pour le moment.' : 'Aucun véhicule ne correspond à vos critères.'}</p>
            <span>{voitures.length === 0 ? 'Revenez bientôt, notre sélection évolue régulièrement.' : 'Modifiez ou réinitialisez les filtres.'}</span>
          </div>
        ) : (
          <>
            <p className="voit-count">{filtered.length} véhicule{filtered.length > 1 ? 's' : ''}</p>
            <div className="voitures-grid">
              {filtered.map(v => {
                const photo = mainPhoto(v);
                return (
                  <div className="voiture-card" key={v.id} onClick={() => openLightbox(v)}>
                    <div className="voiture-img-wrap">
                      {photo
                        ? <img src={photo.image} alt={`${v.marque} ${v.modele}`} loading="lazy" />
                        : <div className="voiture-no-photo">Aucune photo</div>
                      }
                      {!v.disponible && <div className="voiture-badge-vendu">Vendu</div>}
                      {v.photos?.length > 1 && (
                        <div className="voiture-photo-count">{v.photos.length} photos</div>
                      )}
                      <div className="voiture-overlay"><span>Voir le détail</span></div>
                    </div>
                    <div className="voiture-info">
                      <div className="voiture-info-top">
                        <h3>{v.marque} {v.modele}</h3>
                      </div>
                      <div className="voiture-prix">{formatPrix(v.prix)}</div>
                      <div className="voiture-specs">
                        <span>{v.annee}</span>
                        <span>{formatKm(v.kilometrage)}</span>
                        <span>{CARBURANT_LABELS[v.carburant]}</span>
                        <span>{BOITE_LABELS[v.boite]}</span>
                      </div>
                      {v.description && <p className="voiture-desc">{v.description}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* ── Lightbox ── */}
      {selected && (
        <div className="lightbox" onClick={() => setSelected(null)}>
          <div className="lightbox-inner" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setSelected(null)}>✕</button>

            <div className="lightbox-gallery" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
              {selected.photos?.length > 0 ? (
                <>
                  <img src={selected.photos[photoIdx].image} alt={`${selected.marque} ${selected.modele}`} />
                  {selected.photos.length > 1 && (
                    <>
                      <button className="lightbox-nav prev" onClick={prev}>‹</button>
                      <button className="lightbox-nav next" onClick={next}>›</button>
                      <div className="lightbox-dots">
                        {selected.photos.map((_, i) => (
                          <button
                            key={i}
                            className={`lightbox-dot${i === photoIdx ? ' active' : ''}`}
                            onClick={e => { e.stopPropagation(); setPhotoIdx(i); }}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="lightbox-no-photo">Aucune photo disponible</div>
              )}
            </div>

            <div className="lightbox-info">
              <div className="lightbox-header">
                <div>
                  <h3>{selected.marque} {selected.modele}</h3>
                  <p className="lightbox-annee">{selected.annee}</p>
                </div>
                <div className="lightbox-prix">{formatPrix(selected.prix)}</div>
              </div>
              <div className="lightbox-specs">
                {[
                  selected.type_annonce ? { label: 'Type', value: TYPE_ANNONCE_LABELS[selected.type_annonce] } : null,
                  { label: 'Kilométrage', value: formatKm(selected.kilometrage) },
                  { label: 'Carburant',   value: CARBURANT_LABELS[selected.carburant] },
                  { label: 'Boîte',       value: BOITE_LABELS[selected.boite] },
                ].filter(Boolean).map(({ label, value }) => (
                  <div className="lightbox-spec" key={label}>
                    <span className="spec-label">{label}</span>
                    <span className="spec-value">{value}</span>
                  </div>
                ))}
              </div>
              {selected.description && <p className="lightbox-desc">{selected.description}</p>}
              <a href="tel:+33676796771" className="lightbox-cta">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
                </svg>
                Intéressé ? Appelez-nous : 06 76 79 67 71
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Voitures;
